import { useState, useContext, useEffect, forwardRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Typography,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import request from "../../../utilities/requests";
import { EventContext } from "../../../contexts/EventContext";
import { toast } from "../../../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const steps = [
  "Select Ticket to Transfer",
  "Select New Event",
  "Select New Ticket",
  "Add Notes (Optional)",
  "Review & Confirm",
];

const TransferDialog = ({ open, close, user }) => {
  const { pairingEvent, userData } = useContext(EventContext);
  const [canTransfer, setCanTransfer] = useState(false);
  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [order, setOrder] = useState(null);
  const [oldTicketId, setOldTicketId] = useState(null);
  const [newTicketId, setNewTicketId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [transferCheckIn, setTransferCheckin] = useState(true);
  const [step, setStep] = useState(0);
  const [oldTicketName, setOldTicketName] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (open && pairingEvent) {
      setCanTransfer(true);
      getPairingEvents();
      getOrder();
      setStep(0);
      setOldTicketId(null);
      setSelectedEvent(null);
      setNewTicketId(null);
      setTickets([]);
      setTransferNotes("");
    }
  }, [open]);

  const getPairingEvents = async () => {
    let url = 'pairingevent'
    if (userData?.role === "superadmin") {
      let org = pairingEvent?.event?.org
      url = url + `/?orgId=${org}`;
    }
    const result = await request(url);

    if (result.success) {
      const pEvents = result.pairingEvents
        .map((e) => e.event)
        .filter((e) => e._id !== pairingEvent.event._id);
      setEvents(pEvents);
    }
  };

  const getOrder = async () => {
    const body = { eventId: pairingEvent.event._id, userId: user._id };
    const result = await request("ticketing/findorder", "post", body);
    if (result.success) {
      setOrder(result.order);
    } else {
      setCanTransfer(false);
      toast.error(
        "Unable to find an existing ticket - cannot transfer. Please just add the attendee to the new event."
      );
    }
  };

  const getTicketTypes = async (event) => {
    const result = await request(
      `ticketing/tickets/availability/?eventId=${event?._id}`
    );
    if (result?.success) {
      setTickets(result.data || []);
    } else {
      toast("Unable to load tickets");
      console.log("[AssignTicket] getTickets not successful", result);
    }
  };

  const formatDate = (date) => {
    try {
      return date.split("T")[0];
    } catch (e) {
      return date;
    }
  };

  const formatMoney = (cents, currency = "USD") => {
    if (typeof cents !== "number") return "";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const handleTransfer = async () => {
    const body = {
      userId: user._id,
      fromEventId: pairingEvent.event._id,
      toEventId: selectedEvent,
      fromTicketTypeId: oldTicketId,
      toTicketTypeId: newTicketId,
      notes: transferNotes
    }
    setLoading(true);
    const result = await request('ticketing/transfer','post',body);
    if (result.success) {
      toast.success('Attendee transferred successfully');
      handleClose();
    };
    setLoading(false);
  };

  const handleClose = () => {
    setEvents([]);
    setSelectedEvent(null);
    setCanTransfer(false);
    setStep(0);
    setOldTicketId(null);
    setNewTicketId(null);
    setTickets([]);
    setTransferNotes("");
    close();
  };

  const handleNext = (manualOldTicketId, manualEvent, manualNewTicketId) => {
    const oldTicketToCheck = manualOldTicketId ?? oldTicketId;
    const eventToCheck = manualEvent ?? selectedEvent;
    const newTicketToCheck = manualNewTicketId ?? newTicketId;

    if (step === 0 && !oldTicketToCheck) {
      toast.error("Please select a ticket to transfer.");
      return;
    }
    if (step === 1 && !eventToCheck) {
      toast.error("Please select a new event.");
      return;
    }
    if (step === 2 && newTicketToCheck === null) {
      toast.error("Please select a new ticket.");
      return;
    }
    setStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };



  // Find the new ticket name for summary
  let newTicketName = "";
  if (newTicketId && newTicketId !== "noTicket") {
    const newTicket = tickets.find((t) => t.id === newTicketId);
    newTicketName = newTicket ? newTicket.name : "";
  } else if (newTicketId === "noTicket") {
    newTicketName = "No ticket assigned";
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Transfer {user ? user?.profile?.firstName : null}
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent>
        <Stepper activeStep={step} orientation="vertical">
          <Step>
            <StepLabel>{steps[0]}</StepLabel>
            <StepContent>
              <Typography>1) Select the ticket being transferred</Typography>
              {order && (
                <List>
                  {order.items.map((item) => {
                    if (item.assignee.email === order.email) {
                      return (
                        <ListItem key={item.ticketTypeId} disablePadding>
                          <ListItemButton
                            selected={oldTicketId === item.ticketTypeId}
                            onClick={() => {
                              setOldTicketId(item.ticketTypeId);
                              handleNext(item.ticketTypeId);
                              setOldTicketName(item.ticketTypeName);
                            }}
                          >
                            {item.ticketTypeName}
                          </ListItemButton>
                        </ListItem>
                      );
                    }
                    return null;
                  })}
                </List>
              )}
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button disabled>Back</Button>
                <Button variant="contained" onClick={() => handleNext()}>
                  Next
                </Button>
              </DialogActions>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[1]}</StepLabel>
            <StepContent>
              <Typography>
                2) Select an event to which you would like to transfer the attendee
              </Typography>

              <List>
                {events.map((event) => {
                  return (
                    <div key={event._id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                          selected={selectedEvent?._id === event._id}
                          onClick={() => {
                            setSelectedEvent(event);
                            getTicketTypes(event);
                            setNewTicketId(null);
                            handleNext(null, event);
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {event?.name}
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(event?.date)}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                      <Divider />
                    </div>
                  );
                })}
              </List>
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button variant="contained" onClick={() => handleNext()}>
                  Next
                </Button>
              </DialogActions>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[2]}</StepLabel>
            <StepContent>
              <Typography>3) Select the ticket for the new event</Typography>
              {tickets.length > 0 ? (
                <Select
                  labelId="assign-ticket-select-label"
                  id="assign-ticket-select"
                  label="Select Ticket"
                  value={newTicketId || ""}
                  onChange={(e) => {
                    setNewTicketId(e.target.value);
                    handleNext(null, null, e.target.value);
                  }}
                  fullWidth
                >
                  {tickets?.map((t) => (
                    <MenuItem
                      key={t.id}
                      value={t.id}
                      disabled={t.available === 0}
                    >
                      {t.name}
                      {t.available === 0 ? " (Sold out)" : ""}
                      {typeof t.priceCents === "number" && t.currency
                        ? ` — ${formatMoney(t.priceCents, t.currency)}`
                        : ""}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography>No tickets available for the selected event.</Typography>
              )}
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button variant="contained" onClick={() => handleNext()}>
                  Next
                </Button>
              </DialogActions>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[3]}</StepLabel>
            <StepContent>
              <Typography>4) Add notes about the transfer (optional)</Typography>
              <TextField
                label="Transfer Notes"
                multiline
                rows={4}
                fullWidth
                value={transferNotes}
                onChange={(e) => setTransferNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button variant="contained" onClick={() => handleNext()}>
                  Next
                </Button>
              </DialogActions>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>{steps[4]}</StepLabel>
            <StepContent>
              <Typography variant="h6" gutterBottom>
                Review & Confirm
              </Typography>
              <Typography>
                <strong>Old Event:</strong> {pairingEvent?.event?.name || "N/A"}
              </Typography>
              <Typography>
                <strong>Old Ticket:</strong> {oldTicketName || "N/A"}
              </Typography>
              <Typography sx={{ mt: 1 }}>
                <strong>New Event:</strong> {selectedEvent?.name || "N/A"}
              </Typography>
              <Typography>
                <strong>New Ticket:</strong> {newTicketName || "N/A"}
              </Typography>
              {transferNotes && (
                <Typography sx={{ mt: 1 }}>
                  <strong>Notes:</strong> {transferNotes}
                </Typography>
              )}
              <DialogActions sx={{ px: 0, pt: 2 }}>
                <Button onClick={handleBack}>Back</Button>
                <Button
                  variant="contained"
                  onClick={handleTransfer}
                  disabled={!canTransfer}
                  sx={{width: '180px'}}
                >
                  {loading ? <CircularProgress size={20} style={{color: 'white'}}/> : "Confirm Transfer" }
                </Button>
              </DialogActions>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
    </Dialog>
  );
};

export default TransferDialog;
